import styled from 'styled-components'
import {
  Box,
  Tab,
  Tabs,
  Skeleton,
} from '@mui/material'
import { FilterOption } from '../../../components'
import { useUserListActionsModal } from '../../../contexts/UserListActionsModalContext'
import AutoCompleteSearch from '../AutoCompleteSearch'
import { RequestService } from '../../../services/RequestService'
import UserForm from '../UserForm'
import { useTranslation } from "react-i18next"

const TabContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0;
`
const WhoActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FilterOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 150px;
  overflow: auto;
`

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const normalizeTimezone = timezone => {
  return timezone?.toLowerCase() === 'america/sao_paulo'
    ? 'Brasilia'
    : timezone || ''
}

const normalizeRiskLevel = riskLevel => riskLevel?.trim().toLowerCase() || ''

function TabContent({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <TabContentContainer>{children}</TabContentContainer>}
    </div>
  )
}

const CreateUpdateUserTabs = ({
  userData,
  setUserData,
  onChangeTab
}) => {
  const {
    selectedTab,
    setSelectedTab,
    users,
    userList,
    loading,
    removeUserFromList,
    shiftOptions = [],
    userOptions = [],
    timezoneOptions = [],
    caffeineOptions = [],
  } = useUserListActionsModal()

  const { t } = useTranslation('common')
  const handleTabChange = (_, newValue) => {
    setSelectedTab(newValue)
    onChangeTab()
  }

  const handleUserSelect = async (user) => {
    const response = await RequestService.getUserData(user.id)

    let fetchedUser = {}
    if (response.status === 'success') {
      fetchedUser = response.data
    }

    setUserData({
      ...userData,
      id: fetchedUser.id || '',
      name: fetchedUser.name || '',
      company_designated_id: fetchedUser.company_designated_id || '',
      email: fetchedUser.email || '',
      mobile: fetchedUser.mobile_number || '',
      legal_id: fetchedUser.legal_id || '',
      gender: fetchedUser.gender || '',
      birthday: fetchedUser.birthday || '',
      locale: fetchedUser.locale || '',
      timezone: normalizeTimezone(fetchedUser.timezone),
      hired_at: fetchedUser.hired_at || '',
      began_at: fetchedUser.began_at || '',
      removed_at: fetchedUser.removed_at || '',
      keep_data_history: fetchedUser.keep_data_history || '',
      show_in_reports: fetchedUser.show_in_reports ?? '',
      role_name: fetchedUser.role_name || '',
      role_level: fetchedUser.role_level || '',
      role_risk_level: normalizeRiskLevel(fetchedUser.role_risk_level),
      team: fetchedUser.team || '',
      area: fetchedUser.area || '',
      shift: fetchedUser.shift?.id || '',
      password: '',
      supervisor: fetchedUser.supervisor?.map(item => item.id) || [],
      usual_sleep: fetchedUser.usual_sleep || '',
      caffeine: fetchedUser.caffeine || '',
      caffeine_start: fetchedUser.caffeine_start || '',
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs value={selectedTab} onChange={handleTabChange}>
        <Tab label={t('user_actions.create')}/>
        <Tab label={t('user_actions.edit')} />
      </Tabs>

      <TabContent value={selectedTab} index={0}>
        <UserForm
          userData={userData}
          setUserData={setUserData}
          shiftOptions={shiftOptions}
          userOptions={userOptions}
          timezoneOptions={timezoneOptions}
          caffeineOptions={caffeineOptions}
        />
      </TabContent>

      <TabContent value={selectedTab} index={1}>
        <WhoActionContainer>
          <span>{t('user_actions.select_placeholder')}</span>
          <AutoCompleteSearch
            options={userList}
            onSelect={handleUserSelect}
            multiple={false}
          />
          <FilterOptionsContainer>
            {loading ? (
              <>
                <Skeleton>
                  <FilterOption
                    isSelected
                    dismissable
                    name='Jakob Carder'
                  />
                </Skeleton>
                <Skeleton>
                  <FilterOption
                    isSelected
                    dismissable
                    name='Jakob Carder'
                  />
                </Skeleton>
              </>
            ) : (
              users?.map(({ id, name }) => (
                <FilterOption
                  key={id}
                  isSelected
                  dismissable
                  name={name}
                  onClick={() => removeUserFromList({ id })}
                />
              ))
            )}
          </FilterOptionsContainer>
        </WhoActionContainer>
        <UserForm
          userData={userData}
          setUserData={setUserData}
          shiftOptions={shiftOptions}
          userOptions={userOptions}
          timezoneOptions={timezoneOptions}
          caffeineOptions={caffeineOptions}
        />
      </TabContent>
    </Box>
  )
}

export default CreateUpdateUserTabs
